import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as fromProjectsActions from './projects.actions';
import { catchError, concatMap, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin } from 'rxjs';
import { HttpOperation } from '../../enums/http-operation';
import { ProjectModel } from '../../models/project/project.model';
import { EndpointService } from '../../services/endpoints/endpoint.service';
import { HttpErrorService } from '../../services/http-error.service';
import { ProjectStatusFilter } from '../../../pages/project/shared/project-detail-types';
import { ProjectTransformationService } from '../../services/project-transformation.service';

@Injectable()
export class ProjectsEffects {
  fetchProjects$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromProjectsActions.fetchProjects),
      map((action) => action.payload),
      concatMap(({ clientId, projectStatusFilter }) => {
        const endpoint = this.endpointService.getProjectsSlug(HttpOperation.Get);

        let params = new HttpParams();
        if (clientId) {
          params = params.append('client_id', clientId);
        }

        if (projectStatusFilter) {
          const isArchived = projectStatusFilter === ProjectStatusFilter.Archived;
          params = params.append('is_archived', isArchived);
        }

        return this.http.get<unknown>(endpoint, { params }).pipe(
          switchMap((jsonArr: unknown[]) =>
            forkJoin(jsonArr.map((projectPlain) => this.plainToInstance$(projectPlain))),
          ),
          map((projects: ProjectModel[]) => fromProjectsActions.setProjects({ payload: projects })),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Projects',
              fromProjectsActions.setProjects,
              fromProjectsActions,
              error,
            );
          }),
        );
      }),
    );
  });

  fetchSingleProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromProjectsActions.fetchSingleProject),
      map((data) => data.payload),
      switchMap((projectId) => {
        const endpoint = this.endpointService.getProjectsSlug(HttpOperation.Get, projectId);

        return this.http.get<unknown>(endpoint).pipe(
          switchMap((json: unknown) => this.plainToInstance$(json)),
          map((project: ProjectModel) => {
            return fromProjectsActions.setSingleProject({ payload: project });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Tasks',
              fromProjectsActions.fetchSingleProject,
              fromProjectsActions,
              error,
            );
          }),
        );
      }),
    );
  });

  addProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromProjectsActions.addProject),
      map((action) => action.payload),
      mergeMap((projectModel) => {
        const endpoint = this.endpointService.getProjectsSlug(HttpOperation.Post);
        const httpProjectModel = this.instanceToPlain(projectModel);
        delete httpProjectModel.id;

        return this.http.post<unknown>(endpoint, httpProjectModel).pipe(
          switchMap((json: unknown) => this.plainToInstance$(json)),
          map((project: ProjectModel) => fromProjectsActions.projectAdded({ payload: project })),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Projects',
              fromProjectsActions.addProject,
              fromProjectsActions,
              error,
            );
          }),
        );
      }),
    );
  });

  updateProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromProjectsActions.updateProject),
      map((action) => action.payload),
      mergeMap((projectModel) => {
        const endpoint = this.endpointService.getProjectsSlug(HttpOperation.Put, projectModel.id!);

        return this.http.put(endpoint, this.instanceToPlain(projectModel)).pipe(
          switchMap((json: unknown) => this.plainToInstance$(json)),
          map((project: ProjectModel) => fromProjectsActions.projectUpdated({ payload: project })),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Projects',
              fromProjectsActions.updateProject,
              fromProjectsActions,
              error,
            );
          }),
        );
      }),
    );
  });

  deleteProject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromProjectsActions.deleteProject),
      map((action) => action.payload),
      mergeMap((projectModel) => {
        const endpoint = this.endpointService.getProjectsSlug(
          HttpOperation.Delete,
          projectModel.id!,
        );

        return this.http.delete(endpoint, projectModel.id!).pipe(
          map(() => {
            return fromProjectsActions.projectDeleted({ payload: projectModel });
          }),
          catchError((error) => {
            return this.httpErrorService.handleError(
              'Projects',
              fromProjectsActions.deleteProject,
              fromProjectsActions,
              error,
            );
          }),
        );
      }),
    );
  });

  constructor(
    private readonly actions$: Actions,
    private readonly http: HttpClient,
    private readonly endpointService: EndpointService,
    private readonly httpErrorService: HttpErrorService,
    private readonly projectTransformationService: ProjectTransformationService,
  ) {}

  private plainToInstance$(json: any): Observable<ProjectModel> {
    const project = plainToInstance(ProjectModel, json);

    return this.projectTransformationService
      .plainToProjectMemberWrapper(project.projectMemberPropertiesWrapper as any)
      .pipe(
        take(1),
        map((wrappers) => {
          project.projectMemberPropertiesWrapper = wrappers;
          return project;
        }),
      );
  }

  private instanceToPlain(project: ProjectModel): any {
    const httpProject = instanceToPlain(project);
    httpProject.member_properties = this.projectTransformationService.projectMemberWrapperToPlain([
      ...project.projectMemberPropertiesWrapper,
    ]);
    return httpProject;
  }
}
